// store
import * as DebtsStore from "../../../../redux/middleware/CashFlow-Debts.Middleware";
import * as ProtocolStore from "../../../../redux/middleware/MedicalProtocolMiddleware";
import * as PersonStore from "../../../../redux/middleware/Persons.Middleware";

// models
import { AppState } from "../../../../redux/reducers";
import { Debt } from "../../../../models/Entities/CashFlow/Debt/Debt";
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { ErrorType } from "../../../../models/HttpError";

import { makeStyles, tokens } from "@fluentui/react-components";
import * as jwt_decode from "jwt-decode";

import { GetPersonByIdentifier } from "../Debt.Form/Debt.Form.Functions";

//#region Props

interface IProps {
    DebtId: number | undefined;
    isOpen: boolean;
    onClose: () => void;
}

interface ConnectedProps {
    CurrentDebt: Debt | undefined;
    CurrentProtocol: Protocol | undefined;
    Debtor: Person | undefined;
    Creditor: Person | undefined;
    OwnerId: string;

    isLoading: boolean;

    isCheckingDebtStatus: boolean;
    checkedDebtStatusId: number | undefined;
    successCheckingDebtStatus: boolean;
    debtStatusResult: boolean | undefined;

    error: ErrorType | undefined;
}

interface DispatchProps {
    GetDebtById: typeof DebtsStore.actionCreators.GetDebtById;
    CheckDebtStatus: typeof DebtsStore.actionCreators.CheckDebtStatus;
    GetAllPersons: typeof PersonStore.actionCreators.GetAllPersons;
    GetAllMedicalProtocols: typeof ProtocolStore.actionCreators.GetAllMedicalProtocols;
}

export type Props = IProps & ConnectedProps & DispatchProps;

//#endregion

export const mapStateToProps = (state: AppState, ownProps: IProps): ConnectedProps => {
    const debtorList: Debt[] = state?.CashFlowDebts?.DebtsAsDebtor ?? [];
    const creditorList: Debt[] = state?.CashFlowDebts?.DebtsAsCreditor ?? [];

    const currentDebt: Debt | undefined = ownProps.DebtId
        ? debtorList.find((d) => d.id === ownProps.DebtId) ??
          creditorList.find((d) => d.id === ownProps.DebtId)
        : undefined;

    const decoded = (state?.oidc?.user?.access_token
        ? (jwt_decode.jwtDecode(state.oidc.user.access_token) as { tenantId?: string })
        : undefined);
    const currentOwner: string = decoded?.tenantId ?? "";

    const persons: Person[] = state.Persons?.list ?? [];

    const debtor = currentDebt
        ? GetPersonByIdentifier(persons, currentOwner, currentDebt.debtor)
        : undefined;

    const creditor = currentDebt
        ? GetPersonByIdentifier(persons, currentOwner, currentDebt.creditor)
        : undefined;

    const currentProtocol: Protocol | undefined = currentDebt?.protocolId
        ? state.MedicalProtocols?.list?.find((p: Protocol) => p.id === currentDebt.protocolId)
        : undefined;

    return {
        CurrentDebt: currentDebt,
        CurrentProtocol: currentProtocol,
        Debtor: debtor,
        Creditor: creditor,
        OwnerId: currentOwner,

        isLoading: state.CashFlowDebts?.isLoadingOne ?? false,

        isCheckingDebtStatus: state.CashFlowDebts?.isCheckingDebtStatus ?? false,
        checkedDebtStatusId: state.CashFlowDebts?.checkedDebtStatusId,
        successCheckingDebtStatus: state.CashFlowDebts?.successCheckingDebtStatus ?? false,
        debtStatusResult: state.CashFlowDebts?.debtStatusResult,

        error: state.CashFlowDebts?.error,
    };
};

export const mapDispatchToProps = {
    GetDebtById: DebtsStore.actionCreators.GetDebtById,
    CheckDebtStatus: DebtsStore.actionCreators.CheckDebtStatus,
    GetAllPersons: PersonStore.actionCreators.GetAllPersons,
    GetAllMedicalProtocols: ProtocolStore.actionCreators.GetAllMedicalProtocols,
};

//#region Styles

export const useStyles = makeStyles({
    drawerBody: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        paddingTop: "8px",
        paddingBottom: "8px",
    },
    headerCard: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "16px",
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
    },
    headerTitle: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    primaryAmount: {
        fontSize: "28px",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: tokens.colorNeutralForeground1,
    },
    progressTrack: {
        position: "relative",
        height: "8px",
        width: "100%",
        backgroundColor: tokens.colorNeutralBackground4,
        borderRadius: tokens.borderRadiusSmall,
        overflow: "hidden",
    },
    progressFillSettled: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: tokens.colorPaletteGreenForeground2,
    },
    progressFillPending: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: tokens.colorPaletteMarigoldForeground2,
    },
    progressLegend: {
        display: "flex",
        justifyContent: "space-between",
        color: tokens.colorNeutralForeground3,
    },
    sectionCard: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        padding: "14px 16px",
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: tokens.colorNeutralForeground2,
    },
    grid2: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: "12px",
        "@media (max-width: 520px)": {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    fieldLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
    },
    fieldValue: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        fontWeight: 500,
        fontVariantNumeric: "tabular-nums",
    },
    partyRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minWidth: 0,
    },
    partyMeta: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: 1,
    },
    partyLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
    },
    partyName: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase400,
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    partyIdentifier: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "break-all",
        whiteSpace: "nowrap",
        display: "block",
        maxWidth: "100%",
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
    },
    commentsBox: {
        padding: "10px 12px",
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorNeutralForeground2,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    emptyText: {
        color: tokens.colorNeutralForeground3,
        fontStyle: "italic",
    },
    loadingWrap: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
    },
});

//#endregion

// helpers
export const formatMoney = (
    amount: number | undefined | null,
    currency: string | undefined,
    fallbackCurrency: string = "ARS"
): string => {
    const value = amount ?? 0;
    const code = currency || fallbackCurrency;
    try {
        return new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: code,
            minimumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${code} ${value.toFixed(2)}`;
    }
};
